import * as React from "react";
import {useState} from "react";
import {useHookClass} from "@dvrt/graphql-widgets";
import DataSource from "./DataSource";
import {Tab, Tabs} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import Loading from "@/components/breadcrumbs/Loading";
import UpdateExtensionProfileForm from "./UpdateExtensionProfileForm";
import {useDispatch} from "react-redux";
import {formPagesWidth} from "@/constants";
import {useActivateExtensionProfileMutation} from "./queries.generated";
import {showNotification} from "@/actions";
import {FormattedMessage, defineMessage} from "react-intl";
import {Colors} from "@/styles";
import {DataSourceContext, RestrictedModeContext, useDataSource} from "./tools";

const useStyles = makeStyles()(() => ({
    activeProfileTag: {
        marginLeft: 5,
        fontSize: "0.6em",
        backgroundColor: Colors.Base,
        color: "white",
        padding: "1px 3px 0px",
        borderRadius: 3,
    },
}));

const TabLabel = ({name, active}: {name: string; active: boolean}) => {
    const {classes} = useStyles();

    return (
        <div style={{display: "flex", alignItems: "center"}}>
            {name}
            {active && (
                <span className={classes.activeProfileTag}>
                    <FormattedMessage id="active" defaultMessage="active" />
                </span>
            )}
        </div>
    );
};

const UpdateExtensionProfilesContent = () => {
    const dataSource = useDataSource();
    const [tabIndex, setTabIndex] = useState(dataSource.activeProfileId);
    const [activateProfile] = useActivateExtensionProfileMutation();
    const dispatch = useDispatch();
    const [activeProfileId, setActiveProfileId] = useState(dataSource.activeProfileId);

    const profiles = dataSource.data.current.profiles;

    const onSelectProfile = (_event: React.ChangeEvent<object>, value: any) => {
        setTabIndex(value);
    };

    const profileActivator = (profileId: number) => async () => {
        await activateProfile({variables: {id: profileId}});
        dispatch(
            showNotification(
                defineMessage({id: "extensionProfileActivation.success", defaultMessage: "Profile has been activated"}),
            ),
        );
        setActiveProfileId(profileId);
    };

    return (
        <div style={{maxWidth: formPagesWidth + 600}}>
            <Tabs value={tabIndex} onChange={onSelectProfile} variant="fullWidth" indicatorColor="primary">
                {profiles.map((p) => (
                    <Tab
                        value={p.id}
                        key={p.id}
                        label={<TabLabel name={p.name} active={p.id === activeProfileId} />}
                        style={{borderBottom: `2px solid #ddd`}}
                    />
                ))}
            </Tabs>
            {profiles.map((p) => (
                <>
                    {tabIndex === p.id && (
                        <UpdateExtensionProfileForm
                            key={p.id}
                            profileId={p.id}
                            activate={profileActivator(p.id)}
                            isActive={p.id === activeProfileId}
                            otherProfiles={profiles.filter((profile) => profile.id != tabIndex)}
                        />
                    )}
                </>
            ))}
        </div>
    );
};

const UpdateExtensionProfilesPage = ({
    extensionId,
    restrictedAccess = false,
}: {
    extensionId: number;
    restrictedAccess?: boolean;
}) => {
    const dataSource = useHookClass(DataSource, extensionId);

    if (!dataSource.data.current.profiles.length) return <Loading />;

    return (
        <DataSourceContext.Provider value={dataSource}>
            <RestrictedModeContext.Provider value={restrictedAccess}>
                <UpdateExtensionProfilesContent />
            </RestrictedModeContext.Provider>
        </DataSourceContext.Provider>
    );
};

export default UpdateExtensionProfilesPage;
